import { APIClient } from "../helpers/api_helper";
import { ApiResponse } from "../types/api";

const api = new APIClient();

export const SESSION_CAPABILITIES_RESOURCE = "/session/capabilities";

export type PlanFeatureKey =
  | "inventory"
  | "integrations_app_store"
  | "whatsapp_lite"
  | "commissions"
  | "expenses_dre"
  | "client_import"
  | "order_execution_notes"
  | "anamnesis"
  | "appointment_deposit_pix"
  | "extra_users"
  | "crm";

export interface PlanFeaturesPayload {
  features: Partial<Record<PlanFeatureKey, boolean>> & Record<string, boolean>;
  max_services: number | null;
  services_count: number;
  crm_max_pipelines?: number | null;
  crm_pipelines_count?: number;
  crm_max_open_deals?: number | null;
  crm_open_deals_count?: number;
  is_trial: boolean;
  trial_ends_at: string | null;
  trial_days_remaining: number | null;
}

/** Features shown in subscription UX and Premium menu soft-lock. */
export const PLAN_FEATURE_LABEL_KEYS: Record<PlanFeatureKey, string> = {
  inventory: "plan.features.inventory",
  integrations_app_store: "plan.features.integrations_app_store",
  whatsapp_lite: "plan.features.whatsapp_lite",
  commissions: "plan.features.commissions",
  expenses_dre: "plan.features.expenses_dre",
  client_import: "plan.features.client_import",
  order_execution_notes: "plan.features.order_execution_notes",
  anamnesis: "plan.features.anamnesis",
  appointment_deposit_pix: "plan.features.appointment_deposit_pix",
  extra_users: "plan.features.extra_users",
  crm: "plan.features.crm",
};

export const PLAN_FEATURE_DISPLAY_ORDER: PlanFeatureKey[] = [
  "expenses_dre",
  "inventory",
  "integrations_app_store",
  "whatsapp_lite",
  "anamnesis",
  "commissions",
  "client_import",
  "order_execution_notes",
  "appointment_deposit_pix",
  "extra_users",
];

export interface SessionCapabilitiesData {
  plan_features: PlanFeaturesPayload;
  observability?: {
    clarity?: {
      enabled: boolean;
      project_id: string | null;
    };
  };
  help_center?: {
    base_url: string;
    contextual_path: string | null;
  };
}

const PLAN_FEATURES_CHANGED_EVENT = "adatis:plan-features-changed";
const CACHE_TTL_MS = 30_000;

let cachedCapabilities: SessionCapabilitiesData | null = null;
let cachedAt = 0;
let inflight: Promise<SessionCapabilitiesData> | null = null;

export const invalidateSessionCapabilities = (): void => {
  cachedCapabilities = null;
  cachedAt = 0;

  if (typeof window !== "undefined") {
    window.dispatchEvent(new Event(PLAN_FEATURES_CHANGED_EVENT));
  }
};

export const subscribePlanFeaturesChanged = (
  listener: () => void
): (() => void) => {
  if (typeof window === "undefined") {
    return () => undefined;
  }

  window.addEventListener(PLAN_FEATURES_CHANGED_EVENT, listener);

  return () => {
    window.removeEventListener(PLAN_FEATURES_CHANGED_EVENT, listener);
  };
};

export const getSessionCapabilities = async (
  force = false
): Promise<SessionCapabilitiesData> => {
  const now = Date.now();

  if (
    !force &&
    cachedCapabilities !== null &&
    now - cachedAt < CACHE_TTL_MS
  ) {
    return cachedCapabilities;
  }

  if (!force && inflight !== null) {
    return inflight;
  }

  inflight = (async () => {
    const response = (await api.get(
      SESSION_CAPABILITIES_RESOURCE
    )) as ApiResponse<SessionCapabilitiesData>;

    cachedCapabilities = response.data;
    cachedAt = Date.now();

    return response.data;
  })();

  try {
    return await inflight;
  } finally {
    inflight = null;
  }
};
